import React from 'lib/react';
import {
  EVGrid, EVTypography, EVButton, EVRefreshOutlinedIcon, EVAlert, EVPropertySearchIcon,
} from 'lib/components';
import { useDispatch, useSelector } from 'lib/redux';
import { useTranslation, Trans } from 'lib/translation';
import clsx from 'lib/clsx';
import useMediaQuery from 'hooks/useMediaQuery';
import PropTypes from 'prop-types';
import LinearProgressWithLabel from 'commonComponents/progress-bar';
import {
  LABELS_FOR_PRODUCT_AVAILABILITY, INGREDIENTS,
} from 'constants/AppConstants';
import { ERROR_LOADING_ADDRESS } from 'features/placeOrder/OrderAddresses.constants';
import Banner from 'commonComponents/banner';
import { isOrderingDisabledSelector, isSubscriptionDashboardV2EnabledSelector } from 'utils/appSelectors.utils';
import { isAuthorized, isEntitledForEvOneIngredient } from 'utils/auth.utils';
import ScrollableSectionComponent from 'commonComponents/scrollableSection';
import PropertyType from './PropertyType.component';
import ProductList from './Product/ProductList.component';
import ProductSelectionSubscription from './Product/ProductSelectionSubscription.component';
import EstimatedTotal from './EstimatedTotal.component';
import OrderStyles from '../OrderStyles';
import ToggleMapButton from './ToggleMapButton';
import MeasurementInstructions from './MeasurementInstructions.component';
import {
  loadOrderPage,
} from '../Order.actions';
import WarningBanner from './WarningBanner/WarningBanner.component';
import SRS from './SRS/SRS.component';
import SRSValidationBannerComponent from './SRS/components/SRSValidationBanner.component';
import UnauthorisedSidePanel from './UnauthorisedSidePanel.component';
import PrePanel from './PrePanel.component';
import PropertyDataDisplay from './propertyData/PropertyDataDisplay.component';
import { PANEL_TYPES } from '../Order.constants';
/**
 * Component that renders all the components for product selection in the right section of Order Page
 */

function InfoContainerComponent({
  isMobile,
  showMap,
  children,
}) {
  const classes = OrderStyles();
  return (
    <EVGrid
      className={clsx(
        isMobile && classes.roundedTop,
        isMobile ? classes.productPanelMobile : classes.sideContainer,
        isMobile && (showMap ? classes.productPanelMobileClosed : classes.productPanelMobileOpen),
      )}
    >
      {children}
    </EVGrid>
  );
}

function OrderingDisabledAlert() {
  const classes = OrderStyles();
  return (
    <EVGrid className={classes.alertMainContainer}>
      <EVAlert
        severity="warning"
        className={classes.alertContainer}
        classes={{ message: classes.orderResolvedMessage, icon: classes.warningAlertIcon }}
      >
        <EVTypography>
          <Trans i18nKey="order__disable_order_msg" />
        </EVTypography>
      </EVAlert>
    </EVGrid>
  );
}

function ReportUnavailabilityBannerComponent() {
  const classes = OrderStyles();
  const { t } = useTranslation();
  return (
    <EVGrid className={classes.bannerContainer}>
      <WarningBanner
        heading={t('order__report_unavailability_banner_heading')}
        body={t('order__report_unavailability_banner_body')}
      />
    </EVGrid>
  );
}

function PropertyDetailsPanel(props) {
  const {
    geocodedAddress,
    imageCoverageInfoLoading,
    imageCoverageInfo,
    isSRSBillingAffiliate,
    isSRSBranchEmployee,
    myevRefreshSrsBilling,
    srsValidation,
    srscustomerVerified,
    mapPoints,
    currentPanelType,
    entitlements,
    primarySubscriptionProductsWithUsage,
  } = useSelector(({
    orderAddresses, userData, featureFlag, srs, order, userEntitlements,
  }) => ({
    geocodedAddress: orderAddresses.geocodedAddress,
    imageCoverageInfoLoading: orderAddresses.imageCoverageInfoLoading,
    imageCoverageInfo: orderAddresses.imageCoverageInfo,
    isSRSBillingAffiliate: userData.initParameters?.isSRSBillingAffiliate,
    isSRSBranchEmployee: userData.initParameters?.isSRSBranchEmployee,
    myevRefreshSrsBilling: featureFlag.myevRefreshSrsBilling,
    srsValidation: srs.srsValidation,
    srscustomerVerified: srs.srsValidation?.customerVerified,
    mapPoints: orderAddresses.mapPoints,
    currentPanelType: order.currentPanelType,
    entitlements: userEntitlements,
    primarySubscriptionProductsWithUsage: order.primarySubscriptionProductsWithUsage ?? [],
  }));
  const { userProducts: userProductsError, initParameters: initParametersError } = useSelector(({ userData }) => userData.error);
  const isOrderingDisabled = useSelector(isOrderingDisabledSelector);
  const isSubscriptionDashboardV2Enabled = useSelector(isSubscriptionDashboardV2EnabledSelector);

  const classes = OrderStyles();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(640);
  const { showMap, setShowMap } = props;
  const dispatch = useDispatch();

  const handleRetry = () => dispatch(loadOrderPage());

  // Check if user is entitled for PROPERTY_DATA_PRE_INSPECTION ingredient
  const hasPropertyDataPreInspectionEntitlement = isEntitledForEvOneIngredient(entitlements, INGREDIENTS.PROPERTY_DATA_PRE_INSPECTION);

  if (!isAuthorized()) return <UnauthorisedSidePanel />;

  if (geocodedAddress) {
    if (initParametersError || userProductsError) {
      return (
        <EVGrid
          className={clsx(
            classes.roundedTop,
            classes.failedToLoadContainer,
          )}
        >
          <EVGrid>
            <EVTypography className={classes.failedToLoadHeading}>{t('order__failed_to_load')}</EVTypography>
            <EVTypography>{t('order__click_to_retry')}</EVTypography>
          </EVGrid>
          <EVButton
            id="product-list-retry-btn"
            variant="contained"
            color="primary"
            classes={{ label: classes.retryLabel }}
            onClick={handleRetry}
          >
            <EVRefreshOutlinedIcon />
            {t('order_history__retry')}
          </EVButton>
        </EVGrid>
      );
    }

    const displayReportUnavailabilityBanner = () => {
      if (imageCoverageInfoLoading) {
        return <LinearProgressWithLabel labels={LABELS_FOR_PRODUCT_AVAILABILITY} />;
      }
      if (imageCoverageInfo !== null && !imageCoverageInfo.isImageCovered) {
        return <ReportUnavailabilityBannerComponent />;
      }
      return null;
    };

    const hasSRSValidationErrors = myevRefreshSrsBilling && isSRSBillingAffiliate && srsValidation.checked && srsValidation.isError;

    const showProductContainer = () => {
      if (myevRefreshSrsBilling && isSRSBranchEmployee && !srscustomerVerified) {
        return false;
      }
      // #genai - moved the chained "if" onto its own line; conditions and order unchanged
      if (imageCoverageInfoLoading || (imageCoverageInfo !== null && !imageCoverageInfo.isImageCovered)) {
        return false;
      }
      if (hasSRSValidationErrors) {
        return false;
      }
      return true;
    };

    const hasAddressLoadErrors = mapPoints.some((mapPoint) => mapPoint?.address?.address === ERROR_LOADING_ADDRESS);

    const structureMeasurementsPanelContent = (
      <>
        <ScrollableSectionComponent>
          <EVGrid className={classes.scrollableItems}>
            <SRS />
            {displayReportUnavailabilityBanner()}
            {hasSRSValidationErrors && <SRSValidationBannerComponent />}
            {showProductContainer() && (
            <>
              <PropertyType />
              {isSubscriptionDashboardV2Enabled ? <ProductSelectionSubscription /> : <ProductList />}
            </>
            )}
          </EVGrid>
        </ScrollableSectionComponent>
        {showProductContainer() && (
          <EVGrid className={classes.estimationAndSubmissionContainer}>
            {!(isSubscriptionDashboardV2Enabled && primarySubscriptionProductsWithUsage?.some((p) => p.isSubscription))
              && <MeasurementInstructions />}
            <EstimatedTotal />
          </EVGrid>
        )}
      </>
    );

    // Handle different panel types
    /**
     * Determines which panel content to render based on user entitlements and current panel type.
     * - If the user does NOT have the PROPERTY_DATA_PRE_INSPECTION entitlement,
     *   or if the current panel is STRUCTURE_MEASUREMENTS, show the structure measurements panel.
     * - If the current panel is PRE_PANEL, show the PrePanel component.
     * - If the current panel is PROPERTY_DATA, show the PropertyDataDisplay component.
     * - Otherwise, default to the structure measurements panel.
     */
    const renderPanelContent = () => {
      // Show ordering disabled alert if user is ordering disabled
      if (isOrderingDisabled) return <OrderingDisabledAlert />;

      // Show structure measurements if user lacks entitlement or is on STRUCTURE_MEASUREMENTS panel
      if (!hasPropertyDataPreInspectionEntitlement || currentPanelType === PANEL_TYPES.STRUCTURE_MEASUREMENTS) {
        return structureMeasurementsPanelContent;
      }

      // Show PrePanel if current panel type is PRE_PANEL
      if (currentPanelType === PANEL_TYPES.PRE_PANEL) {
        return (
          <ScrollableSectionComponent className={classes.prePanelScrollableSection}>
            <PrePanel />
          </ScrollableSectionComponent>
        );
      }

      // Show PropertyDataDisplay if current panel type is PROPERTY_DATA
      if (currentPanelType === PANEL_TYPES.PROPERTY_DATA) {
        return (
          <ScrollableSectionComponent>
            <PropertyDataDisplay />
          </ScrollableSectionComponent>
        );
      }

      // Default: show structure measurements panel
      return structureMeasurementsPanelContent;
    };

    return (
      <InfoContainerComponent isMobile={isMobile} showMap={showMap} isSRSBranchEmployee={isSRSBranchEmployee}>
        {isMobile && <ToggleMapButton handleClick={() => setShowMap(!showMap)} showMap={showMap} />}
        {hasAddressLoadErrors && <Banner severity="error" message={t('order__address_load_error')} />}
        {renderPanelContent()}
      </InfoContainerComponent>
    );
  }

  return (
    <InfoContainerComponent isMobile={isMobile} showMap={showMap} isSRSBranchEmployee={isSRSBranchEmployee}>
      {isMobile && <ToggleMapButton handleClick={() => setShowMap(!showMap)} showMap={showMap} />}
      <EVGrid className={classes.searchContainer}>
        <SRS />
        {
          isOrderingDisabled
            ? <OrderingDisabledAlert />
            : (
              <EVGrid className={classes.searchPromptContainer}>
                <>
                  <EVPropertySearchIcon htmlColor="#82899C" className={classes.searchPromptImg} />
                  <EVTypography className={classes.searchPromptText}>{t('SearchPrompt')}</EVTypography>
                </>
              </EVGrid>
            )
        }
      </EVGrid>
    </InfoContainerComponent>
  );
}

PropertyDetailsPanel.propTypes = {
  showMap: PropTypes.bool.isRequired,
  setShowMap: PropTypes.func.isRequired,
};

InfoContainerComponent.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  showMap: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

export default PropertyDetailsPanel;
